import { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, AlertTriangle, MapPin, Phone, Instagram, CheckCircle2 } from "lucide-react";
import { RISK_LABEL_MAP, FIELD_TO_LABELS } from "@/lib/riskLabels";

// Formdaki seçeneklerin okunabilir karşılıkları
const V = {
  evet: "Evet",
  hayir: "Hayır",
  bilmiyorum: "Bilmiyorum",
  kararsiz: "Kararsız",
  kira: "Kirada",
  kendi: "Kendi evi",
  yuz_yuze: "Yüz yüze",
  online: "Online",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  apartman: "Apartman dairesi",
  mustakil: "Müstakil ev",
  bahceli: "Bahçeli ev",
  diger: "Diğer",
  ev_ici: "Ev içinde",
  bahce: "Bahçede",
  balkon: "Balkonda",
};

const ig = (v) => (v ? String(v).replace(/^@+/, "") : "");

const txt = (v) => {
  if (v === true) return "Evet";
  if (v === false) return "Hayır";
  if (v === null || v === undefined || v === "") return "—";
  return V[v] || String(v);
};

const SECTIONS = (app) => [
  {
    title: "Uygunluk ve İletişim",
    rows: [
      ["Yaşadığın şehir / ilçe / semt", [app.sehir, app.ilce, app.semt].filter(Boolean).join(", ") || "—", "sehir"],
      ["Yaşın", app.yas ? `${app.yas}` : "—", "yas"],
      ...(Number(app.yas) < 25 ? [
        ["25 yaş altı — durumunu nasıl açıklarsın?", app.yas_25_alti_aciklama],
        ["25 yaş altı — sorumluluğu nasıl üstleneceksin?", app.yas_25_alti_sorumluluk],
      ] : []),
      ["Görüşme tercihi", txt(app.gorusme_tercihi), "gorusme_tercihi"],
      ["Yurt dışından mı başvuruyorsun?", txt(app.yurt_disindan_basvuru), "yurt_disindan_basvuru"],
      ["Referansın var mı?", app.referans_var ? `Evet — ${app.referans_bilgisi || "bilgi girilmemiş"}` : "Hayır"],
      ["İletişim tercihi", app.iletisim_tercihi === "whatsapp" ? `WhatsApp — ${app.telefon || "—"}` : `Instagram — @${ig(app.instagram_kullanici_adi) || "—"}`],
      ["Uygun saat aralığı", app.uygun_saat_araligi || "Belirtilmedi"],
      ["E-posta", app.email || "—"],
      ["Randevusuz ziyaret beklentin var mı?", txt(app.randevusuz_ziyaret_beklentisi), "randevusuz_ziyaret_beklentisi"],
    ],
  },
  {
    title: "Yaşam Koşulları",
    rows: [
      ["Ev tipi", txt(app.ev_tipi)],
      ["Ev durumu", txt(app.ev_durumu)],
      ...(app.ev_durumu === "kira" ? [["Ev sahibi evcil hayvana izin veriyor mu?", txt(app.ev_sahibi_izni), "ev_sahibi_izni"]] : []),
      ["Bina yönetiminin kısıtı var mı?", txt(app.bina_yonetimi_kisiti), "bina_yonetimi_kisiti"],
      ["Köpek günde kaç saat yalnız kalacak?", app.yalniz_kalma_saat != null ? `${app.yalniz_kalma_saat} saat` : "—", "yalniz_kalma_saat"],
      ["Köpek evin neresinde yaşayacak?", txt(app.kopek_nerede_yasayacak), "kopek_nerede_yasayacak"],
    ],
  },
  {
    title: "Ev Halkı",
    rows: [
      ["Evde kaç kişi yaşıyor?", app.evde_kisi_sayisi != null ? `${app.evde_kisi_sayisi} kişi` : "—"],
      ["Evdeki herkes köpek istiyor mu?", txt(app.evde_herkes_istiyor_mu), "evde_herkes_istiyor_mu"],
      ["Evde çocuk var mı?", app.cocuk_var_mi ? `Evet — yaşlar: ${app.cocuk_yaslari || "belirtilmedi"}` : "Hayır", "cocuk_var_mi"],
      ...(app.cocuk_var_mi ? [["Çocukların yaşları", app.cocuk_yaslari || "Belirtilmedi", "cocuk_yaslari"]] : []),
      ["Evde hayvan alerjisi olan var mı?", txt(app.alerji_var_mi), "alerji_var_mi"],
    ],
  },
  {
    title: "Evdeki Diğer Hayvanlar",
    rows: [
      ["Kedi var mı?", txt(app.kedi_var_mi)],
      ["Köpek var mı?", txt(app.kopek_var_mi)],
      ...(app.kedi_var_mi || app.kopek_var_mi ? [
        ["Mevcut hayvanlar aşılı ve kısır mı?", txt(app.mevcut_hayvan_asi_kisir), "mevcut_hayvan_asi_kisir"],
        ["Tanıştırma planın nedir?", app.tanistirma_plani, "tanistirma_plani"],
      ] : []),
    ],
  },
  {
    title: "Günlük Hayat ve Bakım Planı",
    rows: [
      ["Köpekle kim ilgilenecek?", app.kim_ilgilenecek],
      ["Tatilde/seyahatte kim bakacak?", app.tatilde_kim_bakacak],
      ["Günde kaç kez yürüyüşe çıkacak?", app.yuruyus_kac_kez != null ? `${app.yuruyus_kac_kez} kez` : "—", "yuruyus_kac_kez"],
      ["Günlük toplam yürüyüş süresi", app.yuruyus_toplam_sure],
    ],
  },
  {
    title: "Deneyim ve Motivasyon",
    rows: [
      ["Daha önce köpek baktın mı?", txt(app.daha_once_kopek_bakti_mi), "daha_once_kopek_bakti_mi"],
      ["Önceki sahiplendirme/bakım deneyimin", app.onceki_sahiplendirme_deneyimi],
      ["Neden sahiplenmek istiyorsun?", app.neden_sahiplenmek_istiyor, "neden_sahiplenmek_istiyor"],
      ["Zorlukla karşılaştığında ne yaparsın?", app.zorluk_deneyimi],
    ],
  },
];

export default function ApplicationQADialog({ app, open, onOpenChange }) {
  // Tek doğruluk kaynağı: sunucunun hesapladığı risk etiketleri (aynı kurallar backend'de)
  const { flags, flagList } = useMemo(() => {
    if (!app) return { flags: {}, flagList: [] };
    const active = (app.risk_labels || []).filter((l) => l !== "ankara_ici");
    const map = {};
    const list = [];
    active.forEach((key) => {
      const info = RISK_LABEL_MAP[key];
      if (!info) return;
      const text = info.detail || info.label;
      list.push({ key, label: info.label, detail: text, color: info.color });
      if (info.field) map[info.field] = text;
    });
    // aynı alana birden fazla etiket düşerse hepsini göster
    Object.entries(FIELD_TO_LABELS).forEach(([field, keys]) => {
      const hits = keys.filter((k) => active.includes(k));
      if (hits.length > 1) {
        map[field] = hits.map((k) => RISK_LABEL_MAP[k].detail || RISK_LABEL_MAP[k].label).join(" · ");
      }
    });
    return { flags: map, flagList: list };
  }, [app]);

  if (!app) return null;
  const sections = SECTIONS(app);

  const copyAll = () => {
    const lines = [`${app.ad_soyad} → ${app.dog_name || "—"}`, ""];
    sections.forEach((s) => {
      lines.push(`— ${s.title} —`);
      s.rows.forEach(([q, a, f]) => lines.push(`${q}: ${a || "—"}${f && flags[f] ? `  [!! ${flags[f]}]` : ""}`));
      lines.push("");
    });
    const text = lines.join("\n");
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        ok ? toast.success("Soru-cevaplar kopyalandı") : toast.error("Kopyalanamadı");
      } catch (e) {
        toast.error("Kopyalanamadı");
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success("Soru-cevaplar kopyalandı")).catch(fallback);
    } else {
      fallback();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] flex flex-col p-0" data-testid="application-qa-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border/50 shrink-0">
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-8">
            <span>{app.ad_soyad}</span>
            <span className="text-muted-foreground">→</span>
            <span className="text-primary">{app.dog_name || "—"}</span>
          </DialogTitle>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground pt-1">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{app.sehir}{app.ilce ? `, ${app.ilce}` : ""}</span>
            {app.telefon && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{app.telefon}</span>}
            {app.instagram_kullanici_adi && <span className="flex items-center gap-1"><Instagram className="w-3 h-3" />@{ig(app.instagram_kullanici_adi)}</span>}
            <Button size="sm" variant="ghost" className="h-6 px-2 ml-auto" onClick={copyAll} data-testid="qa-copy-all">
              <Copy className="w-3 h-3 mr-1" /> Tümünü kopyala
            </Button>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto px-5 py-4 space-y-6" data-testid="application-qa-scroll">
          {flagList.length > 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="qa-flag-summary">
              <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5 mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Cevaplardan çıkan {flagList.length} dikkat noktası
              </p>
              <div className="flex flex-wrap gap-1.5">
                {flagList.map((f) => (
                  <Badge key={f.key} variant="outline" className="bg-white border-amber-300 text-amber-900 text-xs font-normal" title={f.detail}>
                    {f.label}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-xs text-green-800 flex items-center gap-1.5" data-testid="qa-no-flags">
              <CheckCircle2 className="w-3.5 h-3.5" /> Cevaplarda otomatik dikkat noktası çıkmadı.
            </div>
          )}

          {sections.map((section) => (
            <div key={section.title}>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">{section.title}</p>
              <div className="space-y-2">
                {section.rows.map(([question, answer, field]) => {
                  const flag = field && flags[field];
                  return (
                    <div
                      key={question}
                      className={`rounded-lg p-3 border ${flag ? "border-amber-300 bg-amber-50/70" : "border-border/50 bg-muted/20"}`}
                      data-testid={`qa-row${flag ? "-flagged" : ""}`}
                    >
                      <p className="text-xs text-muted-foreground">{question}</p>
                      <p className={`text-sm mt-0.5 whitespace-pre-wrap ${flag ? "text-amber-950 font-medium" : "font-medium"}`}>
                        {answer || "—"}
                      </p>
                      {flag && (
                        <p className="text-xs text-amber-800 mt-1.5 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 shrink-0" /> {flag}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
